import React, { useState, useCallback, useRef, useEffect } from 'react';
import ReactFlow, {
  addEdge, Background, Controls, MiniMap,
  useNodesState, useEdgesState, Handle, Position,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { TWO_INPUT_TOOLS, cascadePosition } from '../lib/geoprocess-meta';
import { run, isAbortError } from '../lib/worker-pool';
import { useTheme } from '../contexts/ThemeContext';

// Available tool definitions for the palette (fixed category colors — not themed)
const TOOL_PALETTE = [
  { type: 'input-layer', label: 'Input Layer', icon: '📥', color: '#60a5fa', category: 'Input' },
  { type: 'buffer', label: 'Buffer', icon: '⭕', color: '#00d4ff', category: 'Geometry' },
  { type: 'centroid', label: 'Centroids', icon: '⊕', color: '#00d4ff', category: 'Geometry' },
  { type: 'convex', label: 'Convex Hull', icon: '⬡', color: '#00d4ff', category: 'Geometry' },
  { type: 'dissolve', label: 'Dissolve', icon: '∪', color: '#00d4ff', category: 'Geometry' },
  { type: 'clip', label: 'Clip', icon: '✂', color: '#fbbf24', category: 'Overlay' },
  { type: 'union', label: 'Union', icon: '⋃', color: '#fbbf24', category: 'Overlay' },
  { type: 'intersect', label: 'Intersect', icon: '⋂', color: '#fbbf24', category: 'Overlay' },
  { type: 'merge', label: 'Merge', icon: '⊕', color: '#fbbf24', category: 'Overlay' },
  { type: 'filter', label: 'Filter', icon: '🔍', color: '#a78bfa', category: 'Data' },
  { type: 'grid', label: 'Grid', icon: '▦', color: '#34d399', category: 'Generate' },
  { type: 'heatmap', label: 'Heatmap', icon: '🔥', color: '#f87171', category: 'Viz' },
  { type: 'output', label: 'Output Layer', icon: '📤', color: '#34d399', category: 'Output' },
];

// Custom node component
function ToolNode({ data, selected }) {
  const { C: themeC } = useTheme();
  const C = { ...themeC, amber: themeC.warnText };
  const tool = TOOL_PALETTE.find(t => t.type === data.toolType) || {};
  const statusColor = data.status === 'done' ? C.emerald : data.status === 'running' ? C.amber : data.status === 'error' ? C.red : C.text3;

  return (
    <div style={{
      background: C.bg3, border: `2px solid ${selected ? C.cyan : tool.color || C.border}`,
      borderRadius: 10, padding: '10px 14px', minWidth: 140,
      boxShadow: selected ? `0 0 12px ${C.cyan}44` : 'none',
    }}>
      <Handle type="target" position={Position.Left} style={{ background: C.cyan, width: 8, height: 8 }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 16 }}>{tool.icon || '⚙️'}</span>
        <span style={{ fontSize: 12, fontWeight: 600, color: C.text0 }}>{data.label}</span>
      </div>
      {data.params && Object.keys(data.params).length > 0 && (
        <div style={{ fontSize: 10, color: C.text2, marginBottom: 4 }}>
          {Object.entries(data.params).map(([k, v]) => (
            <div key={k}>{k}: <span style={{ color: C.text2 }}>{String(v)}</span></div>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10 }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: statusColor, display: 'inline-block' }} />
        <span style={{ color: statusColor }}>{data.status || 'idle'}</span>
      </div>
      <Handle type="source" position={Position.Right} style={{ background: C.emerald, width: 8, height: 8 }} />
    </div>
  );
}

const nodeTypes = { toolNode: ToolNode };

let nodeId = 0;
const getId = () => `node_${++nodeId}`;

/**
 * ModelBuilder — visual geoprocessing workflow DAG editor
 *
 * Props:
 *   layers - available map layers [{ id, name, geojson }]
 *   onAddResultLayer(geojson, name) - add an executed output to the map
 *   onRunModel(nodes, edges) - notified after the model executes
 *   onClose() - close builder
 */
export default function ModelBuilder({ layers = [], onAddResultLayer, onRunModel, onClose, getViewExtent }) {
  const { C: themeC } = useTheme();
  const C = { ...themeC, amber: themeC.warnText };
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [modelName, setModelName] = useState('Untitled Model');
  const [running, setRunning] = useState(false);
  const [log, setLog] = useState([]);
  const reactFlowRef = useRef(null);
  const aliveRef = useRef(true);   // false after unmount: skip state updates from awaited work
  const abortRef = useRef(null);   // AbortController of the in-flight model run

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
      if (abortRef.current) abortRef.current.abort();
    };
  }, []);

  const onConnect = useCallback((params) => {
    setEdges(eds => addEdge({ ...params, animated: true, style: { stroke: '#00d4ff' } }, eds));
  }, [setEdges]);

  const addNode = (toolType) => {
    const tool = TOOL_PALETTE.find(t => t.type === toolType);
    if (!tool) return;
    const id = getId();
    // Cascade new nodes down-right (lib/geoprocess-meta.js) so they never stack on each other.
    setNodes(nds => [...nds, {
      id,
      type: 'toolNode',
      position: cascadePosition(nds.length),
      data: { label: tool.label, toolType, status: 'idle', params: {} },
    }]);
  };

  const addLog = (msg, type = 'info') => {
    if (!aliveRef.current) return;
    setLog(prev => [...prev.slice(-30), { msg, type, time: new Date().toLocaleTimeString() }]);
  };

  const updateNodeParams = (id, patch) => {
    setNodes(nds => nds.map(n => n.id === id ? { ...n, data: { ...n.data, params: { ...n.data.params, ...patch } } } : n));
  };

  const setNodeStatus = (id, status) => {
    if (!aliveRef.current) return;
    setNodes(nds => nds.map(n => n.id === id ? { ...n, data: { ...n.data, status } } : n));
  };

  const runModel = async () => {
    if (nodes.length === 0) return;
    setRunning(true);
    addLog('▶ Model execution started', 'system');

    // Reset statuses
    setNodes(nds => nds.map(n => ({ ...n, data: { ...n.data, status: 'idle' } })));

    const ctl = new AbortController();
    abortRef.current = ctl;
    const order = topologicalSort(nodes, edges);
    const results = {}; // nodeId → GeoJSON FeatureCollection
    let outputCount = 0;
    let failed = false;

    for (const id of order) {
      const node = nodes.find(n => n.id === id);
      if (!node) continue;
      const { toolType, label, params = {} } = node.data;

      setNodeStatus(id, 'running');
      // Yield to the renderer so status indicators update
      await new Promise(r => setTimeout(r, 30));

      try {
        // Gather inputs from incoming edges (in edge insertion order)
        const inputs = edges
          .filter(e => e.target === id)
          .map(e => results[e.source])
          .filter(Boolean);

        let result;
        if (toolType === 'input-layer') {
          const layer = layers.find(l => l.id === params.layerId);
          if (!layer?.geojson) throw new Error('Select a source layer in node parameters');
          result = { geojson: layer.geojson, summary: `${layer.geojson.features?.length || 0} features from ${layer.name}` };
        } else {
          if (inputs.length === 0 && toolType !== 'grid') throw new Error('No input connected');
          if (TWO_INPUT_TOOLS.includes(toolType) && inputs.length < 2) throw new Error(`${label} needs two connected inputs`);
          // Runs in the worker pool; progress/cancel appear in TaskConsole.
          // A grid without an input covers the current map view (never the whole world).
          const runParams = toolType === 'grid' && inputs.length === 0 ? { ...params, extent: getViewExtent?.() } : params;
          result = await run('executeTool', { toolType, inputs, params: runParams }, { label: `Model: ${label}`, signal: ctl.signal });
        }
        if (!aliveRef.current) return; // unmounted mid-run

        results[id] = result.geojson;

        if (toolType === 'output') {
          outputCount++;
          const name = params.name || `${modelName} — output ${outputCount}`;
          if (onAddResultLayer) onAddResultLayer(result.geojson, name);
          addLog(`📤 Added layer to map: ${name}`, 'success');
        }

        setNodeStatus(id, 'done');
        addLog(`✓ ${label}: ${result.summary}`, 'success');
      } catch (err) {
        if (!aliveRef.current) return;
        setNodeStatus(id, 'error');
        addLog(isAbortError(err) ? `■ ${label}: cancelled` : `✗ ${label}: ${err.message}`, 'error');
        failed = true;
        break;
      }
    }

    if (!aliveRef.current) return;

    // If no explicit output node, surface the final result automatically
    if (!failed && outputCount === 0) {
      const lastId = [...order].reverse().find(id => results[id]);
      if (lastId && onAddResultLayer) {
        onAddResultLayer(results[lastId], `${modelName} — result`);
        addLog('📤 No Output node — final result added to map', 'info');
      }
    }

    addLog(failed ? '✗ Model execution failed' : '✓ Model execution complete', failed ? 'error' : 'success');
    abortRef.current = null;
    if (!aliveRef.current) return;
    setRunning(false);
    if (onRunModel) onRunModel(nodes, edges);
  };

  const clearModel = () => {
    setNodes([]);
    setEdges([]);
    setLog([]);
    nodeId = 0;
  };

  const exportModel = () => {
    const model = { name: modelName, nodes, edges, version: 1 };
    const blob = new Blob([JSON.stringify(model, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${modelName.replace(/\s+/g, '_')}.json`;
    link.click();
    URL.revokeObjectURL(url);
    addLog(`📁 Model exported: ${modelName}`, 'info');
  };

  const importModel = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const model = JSON.parse(ev.target.result);
        if (model.nodes && model.edges) {
          // Keep the module-level id counter ahead of loaded ids (avoid duplicate keys).
          model.nodes.forEach(n => {
            const m = /^node_(\d+)$/.exec(n.id || '');
            if (m) nodeId = Math.max(nodeId, Number(m[1]));
          });
          setNodes(model.nodes);
          setEdges(model.edges);
          setModelName(model.name || 'Imported Model');
          addLog(`📂 Model loaded: ${model.name || 'Imported'}`, 'info');
        }
      } catch { addLog('✗ Invalid model file', 'error'); }
    };
    reader.readAsText(file);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: C.bg0, zIndex: 200, display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ height: 48, background: C.bg1, borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', padding: '0 16px', gap: 12, flexShrink: 0 }}>
        <span style={{ fontSize: 16 }}>🔧</span>
        <input value={modelName} onChange={e => setModelName(e.target.value)}
          style={{ fontSize: 14, fontWeight: 700, color: C.text0, background: 'transparent', border: 'none', outline: 'none', width: 200 }} />
        <span style={{ fontSize: 11, color: C.text3 }}>Model Builder</span>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 11, color: C.text3 }}>{nodes.length} nodes · {edges.length} edges</span>
        <div style={{ width: 1, height: 20, background: C.border }} />
        <button onClick={runModel} disabled={running || nodes.length === 0}
          style={{ fontSize: 12, padding: '5px 14px', borderRadius: 6, background: running ? C.bg4 : 'linear-gradient(135deg,#00b8de,#3b82f6)', border: 'none', color: '#fff', fontWeight: 600, cursor: running ? 'wait' : 'pointer', opacity: (running || nodes.length === 0) ? 0.5 : 1 }}>
          {running ? '⏳ Running...' : '▶ Run'}
        </button>
        {running && (
          <button onClick={() => abortRef.current && abortRef.current.abort()}
            style={{ fontSize: 12, padding: '5px 12px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text1, cursor: 'pointer' }}>■ Stop</button>
        )}
        <button onClick={exportModel} style={{ fontSize: 12, padding: '5px 12px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text1, cursor: 'pointer' }}>💾 Save</button>
        <label style={{ fontSize: 12, padding: '5px 12px', borderRadius: 6, background: C.bg4, border: `1px solid ${C.border}`, color: C.text1, cursor: 'pointer' }}>
          📂 Load
          <input type="file" accept=".json" onChange={importModel} style={{ display: 'none' }} />
        </label>
        <button onClick={clearModel} style={{ fontSize: 12, padding: '5px 12px', borderRadius: 6, background: 'rgba(248,113,113,0.12)', border: `1px solid rgba(248,113,113,0.25)`, color: C.red, cursor: 'pointer' }}>Clear</button>
        <button onClick={onClose} style={{ fontSize: 14, background: 'none', border: 'none', color: C.text3, cursor: 'pointer' }}>✕</button>
      </div>

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Tool palette */}
        <div style={{ width: 180, background: C.bg1, borderRight: `1px solid ${C.border}`, overflow: 'auto', padding: 8, flexShrink: 0 }}>
          <div style={{ fontSize: 10, color: C.text2, textTransform: 'uppercase', fontWeight: 600, marginBottom: 8, padding: '0 6px' }}>Drag to add</div>
          {TOOL_PALETTE.map(tool => (
            <div key={tool.type}
              draggable
              onDragEnd={() => addNode(tool.type)}
              onClick={() => addNode(tool.type)}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', marginBottom: 3, borderRadius: 6, background: C.bg3, border: `1px solid ${C.border}`, cursor: 'grab', transition: 'all .15s' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = tool.color}
              onMouseLeave={e => e.currentTarget.style.borderColor = C.border}>
              <span style={{ fontSize: 14 }}>{tool.icon}</span>
              <div>
                <div style={{ fontSize: 11, fontWeight: 500, color: C.text0 }}>{tool.label}</div>
                <div style={{ fontSize: 9, color: C.text3 }}>{tool.category}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Canvas */}
        <div style={{ flex: 1 }} ref={reactFlowRef}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={(_, node) => setSelectedNode(node)}
            nodeTypes={nodeTypes}
            fitView
            style={{ background: C.bg0 }}
            defaultEdgeOptions={{ animated: true, style: { stroke: C.cyan, strokeWidth: 2 } }}
          >
            <Background color={C.bg4} gap={20} size={1} />
            <Controls style={{ background: C.bg2, borderColor: C.border, borderRadius: 8 }} />
            <MiniMap
              nodeColor={node => {
                const tool = TOOL_PALETTE.find(t => t.type === node.data?.toolType);
                return tool?.color || C.text3;
              }}
              maskColor="rgba(13,17,23,0.8)"
              style={{ background: C.bg2, borderRadius: 8 }}
            />
          </ReactFlow>
        </div>

        {/* Log panel */}
        <div style={{ width: 240, background: C.bg1, borderLeft: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
          <div style={{ padding: '10px 12px', borderBottom: `1px solid ${C.border}`, fontSize: 12, fontWeight: 600, color: C.text0 }}>
            Execution Log
          </div>
          <div style={{ flex: 1, overflow: 'auto', padding: 8 }}>
            {log.length === 0 ? (
              <div style={{ fontSize: 11, color: C.text3, padding: '20px 8px', textAlign: 'center' }}>
                Add nodes, connect them, then click Run.
              </div>
            ) : (
              log.map((entry, i) => (
                <div key={i} style={{ fontSize: 10, color: entry.type === 'error' ? C.red : entry.type === 'success' ? C.emerald : entry.type === 'system' ? C.cyan : C.text2, marginBottom: 3, lineHeight: 1.4 }}>
                  <span style={{ color: C.text3 }}>{entry.time}</span> {entry.msg}
                </div>
              ))
            )}
          </div>
          {/* Selected node info + parameters */}
          {selectedNode && (() => {
            const node = nodes.find(n => n.id === selectedNode.id);
            if (!node) return null;
            const { toolType, label, status, params = {} } = node.data;
            const inputStyle = { width: '100%', fontSize: 10, padding: '4px 6px', borderRadius: 5, background: C.bg4, border: `1px solid ${C.border}`, color: C.text0, outline: 'none', boxSizing: 'border-box' };
            const lblStyle = { fontSize: 9, color: C.text3, display: 'block', marginBottom: 2, marginTop: 6 };
            return (
              <div style={{ padding: '10px 12px', borderTop: `1px solid ${C.border}`, maxHeight: 260, overflow: 'auto' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: C.text0, marginBottom: 4 }}>{label}</div>
                <div style={{ fontSize: 10, color: C.text2 }}>
                  Status: <span style={{ color: status === 'done' ? C.emerald : status === 'running' ? C.amber : status === 'error' ? C.red : C.text2 }}>{status || 'idle'}</span>
                </div>

                {toolType === 'input-layer' && (
                  <>
                    <label style={lblStyle}>Source Layer</label>
                    <select value={params.layerId || ''} onChange={e => updateNodeParams(node.id, { layerId: e.target.value })} style={inputStyle}>
                      <option value="">Select layer...</option>
                      {layers.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                    </select>
                  </>
                )}

                {toolType === 'buffer' && (
                  <>
                    <label style={lblStyle}>Radius</label>
                    <input type="number" value={params.radius || ''} placeholder="1"
                      onChange={e => updateNodeParams(node.id, { radius: e.target.value })} style={inputStyle} />
                    <label style={lblStyle}>Unit</label>
                    <select value={params.unit || 'kilometers'} onChange={e => updateNodeParams(node.id, { unit: e.target.value })} style={inputStyle}>
                      <option value="meters">meters</option>
                      <option value="kilometers">kilometers</option>
                      <option value="miles">miles</option>
                    </select>
                  </>
                )}

                {toolType === 'grid' && (
                  <>
                    <label style={lblStyle}>Grid Type</label>
                    <select value={params.gridType || 'square'} onChange={e => updateNodeParams(node.id, { gridType: e.target.value })} style={inputStyle}>
                      <option value="square">Square</option>
                      <option value="hex">Hexagonal</option>
                    </select>
                    <label style={lblStyle}>Cell Size</label>
                    <input type="number" value={params.cellSize || ''} placeholder="10"
                      onChange={e => updateNodeParams(node.id, { cellSize: e.target.value })} style={inputStyle} />
                    <label style={lblStyle}>Unit</label>
                    <select value={params.unit || 'kilometers'} onChange={e => updateNodeParams(node.id, { unit: e.target.value })} style={inputStyle}>
                      <option value="meters">meters</option>
                      <option value="kilometers">kilometers</option>
                    </select>
                  </>
                )}

                {toolType === 'filter' && (
                  <>
                    <label style={lblStyle}>Attribute</label>
                    <input value={params.attr || ''} placeholder="property name"
                      onChange={e => updateNodeParams(node.id, { attr: e.target.value })} style={inputStyle} />
                    <label style={lblStyle}>Operator</label>
                    <select value={params.op || '='} onChange={e => updateNodeParams(node.id, { op: e.target.value })} style={inputStyle}>
                      {['=', '!=', '>', '<', '>=', '<=', 'contains'].map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                    <label style={lblStyle}>Value</label>
                    <input value={params.value ?? ''} placeholder="value"
                      onChange={e => updateNodeParams(node.id, { value: e.target.value })} style={inputStyle} />
                  </>
                )}

                {toolType === 'output' && (
                  <>
                    <label style={lblStyle}>Output Layer Name</label>
                    <input value={params.name || ''} placeholder="Result layer"
                      onChange={e => updateNodeParams(node.id, { name: e.target.value })} style={inputStyle} />
                  </>
                )}

                {TWO_INPUT_TOOLS.includes(toolType) && (
                  <div style={{ fontSize: 9, color: C.text3, marginTop: 6, lineHeight: 1.4 }}>
                    Connect two inputs to this node{toolType === 'clip' ? ' (first = data, second = mask)' : ''}.
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

/**
 * Topological sort of nodes based on edges (Kahn's algorithm).
 */
function topologicalSort(nodes, edges) {
  const inDegree = {};
  const adj = {};
  nodes.forEach(n => { inDegree[n.id] = 0; adj[n.id] = []; });
  edges.forEach(e => {
    inDegree[e.target] = (inDegree[e.target] || 0) + 1;
    adj[e.source] = adj[e.source] || [];
    adj[e.source].push(e.target);
  });

  const queue = nodes.filter(n => inDegree[n.id] === 0).map(n => n.id);
  const order = [];

  while (queue.length > 0) {
    const id = queue.shift();
    order.push(id);
    (adj[id] || []).forEach(target => {
      inDegree[target]--;
      if (inDegree[target] === 0) queue.push(target);
    });
  }

  // Add any remaining nodes (cycles or disconnected)
  nodes.forEach(n => { if (!order.includes(n.id)) order.push(n.id); });

  return order;
}
